// Privacy values stream contracts: retroactive data minimisation (PB-16-03, owner decision B-17,
// PD-610 – PD-616), the collection-matrix publish confirmation (B-31, PD-615) and the legal basis
// catalogue (H-04 / B-14, PD-617 – PD-621). Messages are i18n keys (PD-015).
import { z } from 'zod';

const BasisCode = z.string().trim().regex(/^[a-z][a-z_]{1,39}$/, 'v.invalid');
const Localised = (max: number) => z.object({ en: z.string().trim().min(1, 'v.required').max(max, 'v.tooLong'), ar: z.string().trim().max(max, 'v.tooLong').optional() });
const ItemCode = z.string().regex(/^(email|phone|organisation|id_document|id_number|field:[a-z][a-z0-9_]{0,39}|document:[a-z][a-z0-9_-]{0,39})$/, 'v.invalid');

// ------------------------------------------------------------------ publish confirmation (B-31)
/**
 * Publishing the collection matrix. When the draft would hide existing values, the publisher must
 * acknowledge how many (the number the confirmation dialog showed); a different number means the
 * data changed since the dialog was opened and the publish is refused (PD-615).
 */
export const CollectionMatrixPublishRequest = z.object({
  expectedVersion: z.number().int().min(1),
  acknowledgedHidden: z.number().int().min(0).optional(),
});

export type CollectionModeValue = 'required' | 'optional' | 'disallowed';

export interface MinimisationImpactResponse {
  readonly draft: { readonly id: string; readonly number: number; readonly version: number };
  readonly publishedNumber: number | null;
  /** Visitor types (code and label; '*' = every type and channels without one) whose rules change. */
  readonly visitorTypes: ReadonlyArray<{ readonly code: string; readonly label: string }>;
  /** Items (fields, documents, identity items) whose rules change for at least one visitor type. */
  readonly items: ReadonlyArray<{ readonly item: string; readonly label: string }>;
  readonly changes: ReadonlyArray<{ readonly category: string; readonly purpose: string | null; readonly item: string; readonly from: CollectionModeValue; readonly to: CollectionModeValue }>;
  /** Existing values that become hidden on publish (records × fields), per item. */
  readonly hide: { readonly total: number; readonly byItem: Readonly<Record<string, number>> };
  /** Values hidden earlier that reappear because the draft allows them again (unless a newer value exists). */
  readonly restore: { readonly total: number; readonly byItem: Readonly<Record<string, number>> };
}

export interface CollectionMatrixPublishResult {
  readonly id: string;
  readonly number: number;
  readonly status: 'published';
  readonly version: number;
  readonly publishedAt: string | null;
  readonly minimisation: { readonly hidden: number; readonly restored: number; readonly revokedExports: number };
}

// ------------------------------------------------------------------ hidden values and reviewed purge (B-17)
export interface MinimisedValueView {
  readonly id: string;
  readonly item: string;
  readonly target: string;
  /** Record class it is retained and purged under. */
  readonly recordClass: string;
  /** Safe identifiers only — never the hidden value. */
  readonly recordId: string;
  readonly personId: string;
  readonly participationId: string | null;
  readonly visitId: string | null;
  readonly siteId: string | null;
  readonly hiddenAt: string;
  readonly hiddenByNumber: number;
  /** False when a newer value was collected since (the held one can no longer come back). */
  readonly restorable: boolean;
}

export interface MinimisationListResponse {
  readonly summary: Readonly<Record<string, number>>;
  readonly total: number;
  readonly items: readonly MinimisedValueView[];
  readonly nextCursor: string | null;
  readonly canPlanPurge: boolean;
}

export const PlanMinimisationPurgeRequest = z.object({
  dryRun: z.boolean().default(false),
  /** Limit the purge to these items; omitted = every hidden value. */
  items: z.array(ItemCode).max(200).optional(),
});

// ------------------------------------------------------------------ legal basis catalogue (B-14 / H-04)
export const SaveLegalBasisRequest = z.object({
  /** The current version number (0 = no organisation version yet: engineering placeholder or new code). */
  expectedNumber: z.number().int().min(0),
  label: Localised(120),
  wording: Localised(4000).nullable(),
  requiresConsent: z.boolean(),
  note: z
    .string()
    .trim()
    .max(500, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
});
export const RetireLegalBasisRequest = z.object({ expectedNumber: z.number().int().min(0), reason: z.string().trim().min(3, 'v.reasonShort').max(1000, 'v.tooLong') });
export const LegalBasisCodeParam = BasisCode;

export interface LegalBasisView {
  readonly code: string;
  readonly label: { readonly en: string; readonly ar?: string };
  readonly wording: { readonly en: string; readonly ar?: string } | null;
  readonly requiresConsent: boolean;
  readonly status: 'active' | 'retired';
  /** engineering_default = shipped placeholder never changed by the organisation (PD-250). */
  readonly source: 'engineering_default' | 'organisation';
  /** Current organisation version (0 for an unchanged placeholder). */
  readonly number: number;
  readonly changedAt: string | null;
  /** Purposes (draft or approved) that name this basis. */
  readonly usedBy: number;
}

export interface LegalBasisListResponse {
  readonly items: readonly LegalBasisView[];
  readonly canManage: boolean;
}
